import { PHILOSOPHERS, TRAITS, MAX_RAW, BASELINE } from "../data/philosophers.js";
import { QUESTIONS } from "../data/questions.js";

// Step 1 — raw scores from selected option indices.
export function computeRaw(answers) {
  const raw = { DIS: 0, CUR: 0, RES: 0, EMP: 0, AMB: 0, SER: 0 };
  answers.forEach((optIdx, qIdx) => {
    if (optIdx == null) return;
    const opt = QUESTIONS[qIdx].options[optIdx];
    Object.entries(opt.deltas).forEach(([t, v]) => {
      raw[t] += v;
    });
  });
  return raw;
}

// Step 2 — normalize each trait to 0..100 (shown in character sheet).
export function normalizeUser(raw) {
  const u = {};
  TRAITS.forEach((t) => {
    u[t] = Math.min(100, (raw[t] / (MAX_RAW[t] * 0.6)) * 100);
  });
  return u;
}

// Step 3 — center against baseline.
function center(user) {
  const c = {};
  TRAITS.forEach((t) => {
    c[t] = user[t] - BASELINE[t];
  });
  return c;
}

// Step 4 — z-score standardize across the 6 trait values of a vector.
function standardize(vec) {
  const vals = TRAITS.map((t) => vec[t]);
  const mean = vals.reduce((a, b) => a + b, 0) / 6;
  const variance = vals.reduce((a, b) => a + (b - mean) ** 2, 0) / 6;
  let std = Math.sqrt(variance);
  if (std === 0) std = 1;
  const z = {};
  TRAITS.forEach((t) => {
    z[t] = (vec[t] - mean) / std;
  });
  return z;
}

// Step 5 — Pearson correlation = sum(zu * zp) / 6.
function correlate(zu, zp) {
  let s = 0;
  TRAITS.forEach((t) => {
    s += zu[t] * zp[t];
  });
  return s / 6;
}

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}

// Full scoring pipeline. Returns { user, matches: [{philosopher, r, pct}] }
export function score(answers) {
  const raw = computeRaw(answers);
  const user = normalizeUser(raw);
  const centered = center(user);
  const zu = standardize(centered);

  const scored = PHILOSOPHERS.map((p) => {
    const zp = standardize(p.vector);
    const r = correlate(zu, zp);
    return { philosopher: p, r };
  });

  scored.sort((a, b) => b.r - a.r);

  const topR = scored[0].r;
  const topPct = clamp(Math.round(80 + topR * 17), 80, 97);

  const matches = scored.map((m, i) => {
    if (i === 0) return { ...m, pct: topPct };
    const f = Math.max(0, (m.r + 1) / (topR + 1));
    let pct = clamp(Math.round(topPct * (0.55 + 0.45 * f)), 5, topPct - 2);
    return { ...m, pct };
  });

  // Force strictly decreasing pct after the top.
  for (let i = 1; i < matches.length; i++) {
    if (matches[i].pct >= matches[i - 1].pct) {
      matches[i].pct = matches[i - 1].pct - 1;
    }
    if (matches[i].pct < 5) matches[i].pct = 5;
  }

  return { user, raw, matches };
}

// Letter grade from normalized user trait value (0..100).
export function gradeFor(value) {
  if (value >= 85) return "A+";
  if (value >= 70) return "A";
  if (value >= 55) return "B+";
  if (value >= 40) return "B";
  if (value >= 25) return "C+";
  return "C";
}
